import { StoryConversion } from '../types'

/**
 * Top produtos e Top vídeos por audiência, direto da última execução.
 *
 * Existe para responder no painel a pergunta que antes só dava para investigar
 * pelo bot ou pelo snapshot mensal: "por que o Top de produtos veio vazio?".
 * O `byProduct` e o `topViewedStories` sempre foram calculados pelo timeline e
 * descartados no caminho — sem eles na tela, "não aparece" era indistinguível
 * de "o pipeline não produziu", e a única saída era adivinhar.
 *
 * Por isso o card mostra o ESTADO antes de mostrar a lista: lista vazia com
 * causa conhecida é diagnóstico; lista vazia sem causa é convite a inventar
 * uma — foi assim que um relatório de cliente saiu atribuindo o vazio à
 * cobertura de widde_uid, que não tem relação nenhuma com este ranking.
 */

export type TopViewedEstado = 'ok' | 'sem_audiencia' | 'falhou' | 'nao_calculado'

export interface ProductRow {
  externalId: string
  title: string | null
  attributedRevenue: number
  orders: number
  buyers: number
  videosCount: number | null
  viewers: number | null
  conversionRate: number | null
  matchProvenance?: string
}

export interface ProductDiagnosticsProps {
  byProduct: ProductRow[] | null
  topViewed: StoryConversion[] | null
  topViewedEstado: TopViewedEstado | null
  topViewedMotivo: string | null
  /** `false` = período anterior a 2026-03: o byProduct sai vazio POR DESENHO. */
  snapshotsAvailable: boolean | null
}

const fmtBRL = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0)
const fmtInt = (v: number) => new Intl.NumberFormat('pt-BR').format(v || 0)

/** Texto do estado. Cada um diz o QUE FAZER, não só o que houve. */
const ESTADO_TEXTO: Record<TopViewedEstado, { rotulo: string; detalhe: string; cor: string }> = {
  ok: { rotulo: 'Calculado', detalhe: 'A leitura de audiência rodou e o ranking saiu.', cor: '#059669' },
  sem_audiencia: {
    rotulo: 'Sem audiência no período',
    detalhe: 'A leitura rodou e não havia nenhuma visualização no recorte. É RESULTADO, não falha — não leia como erro.',
    cor: '#6b7280',
  },
  falhou: {
    rotulo: 'Leitura falhou',
    detalhe: 'A busca de audiência quebrou. NÃO significa que a loja não tem vídeo — significa que não foi possível medir.',
    cor: '#dc2626',
  },
  nao_calculado: {
    rotulo: 'Não calculado (cache)',
    detalhe: 'A timeline veio do cache e não recalcula o ranking. Marque "Ignorar cache" e processe de novo.',
    cor: '#d97706',
  },
}

export function ProductDiagnosticsCard({
  byProduct,
  topViewed,
  topViewedEstado,
  topViewedMotivo,
  snapshotsAvailable,
}: ProductDiagnosticsProps) {
  const estado = topViewedEstado ?? 'nao_calculado'
  const info = ESTADO_TEXTO[estado]

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <h3 style={{ marginTop: 0 }}>Diagnóstico: produtos e audiência</h3>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <span style={{ fontWeight: 600, color: info.cor }}>● {info.rotulo}</span>
        <span style={{ color: '#6b7280', fontSize: 13 }}>{info.detalhe}</span>
      </div>
      {topViewedMotivo && (
        <div style={{ background: '#fef2f2', color: '#991b1b', padding: 8, borderRadius: 6, fontSize: 13, marginBottom: 12 }}>
          {topViewedMotivo}
        </div>
      )}

      <h4>Top produtos por receita atribuída</h4>
      {snapshotsAvailable === false ? (
        // Vazio POR DESENHO: antes de 2026-03 não há snapshot no evento, e o
        // catálogo de hoje não pode responder pelo passado — serviria um Top-N
        // plausível e errado. Dizer isso é melhor que uma lista vazia muda.
        <p style={{ color: '#6b7280', fontSize: 13 }}>
          Indisponível para períodos anteriores a 2026-03 — não há snapshot de produto no evento, e usar o
          catálogo atual atribuiria os produtos de hoje a compras antigas.
        </p>
      ) : !byProduct ? (
        <p style={{ color: '#d97706', fontSize: 13 }}>
          Não calculado nesta execução (cache). Marque &quot;Ignorar cache&quot; e processe de novo.
        </p>
      ) : byProduct.length === 0 ? (
        <p style={{ color: '#6b7280', fontSize: 13 }}>
          Nenhum produto com receita atribuída no período. Verifique se há compras casando com produto de vídeo.
        </p>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>#</th><th>Produto</th><th>Receita atribuída</th><th>Pedidos</th>
              <th>Compradores</th><th>Vídeos</th><th>Taxa</th>
            </tr>
          </thead>
          <tbody>
            {byProduct.slice(0, 20).map((p, i) => (
              <tr key={p.externalId}>
                <td>{i + 1}</td>
                <td>{p.title ?? p.externalId}</td>
                <td>{fmtBRL(p.attributedRevenue)}</td>
                <td>{fmtInt(p.orders)}</td>
                {/* NUNCA somar esta coluna: comprador de 3 produtos conta em 3 linhas. */}
                <td>{fmtInt(p.buyers)}</td>
                <td>{p.videosCount ?? '—'}</td>
                {/* `null` = denominador indisponível. Traço, nunca 0%. */}
                <td>{p.conversionRate != null ? `${(p.conversionRate * 100).toFixed(2)}%` : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p style={{ color: '#6b7280', fontSize: 12, marginTop: 4 }}>
        Crédito por ITEM — a soma reconcilia com a receita atribuída. Não some a coluna de compradores:
        quem comprou 3 produtos aparece em 3 linhas.
      </p>

      <h4 style={{ marginTop: 20 }}>Top vídeos por audiência</h4>
      {!topViewed || topViewed.length === 0 ? (
        <p style={{ color: '#6b7280', fontSize: 13 }}>Sem ranking — ver o estado acima.</p>
      ) : (
        <table className="data-table">
          <thead>
            <tr><th>#</th><th>Vídeo</th><th>Espectadores</th><th>Compradores</th><th>Receita atribuída</th></tr>
          </thead>
          <tbody>
            {topViewed.slice(0, 10).map((s: any, i: number) => (
              <tr key={s.storyKey}>
                <td>{i + 1}</td>
                <td>{s.storyTitle ?? s.storyKey}</td>
                <td>{s.viewers != null ? fmtInt(s.viewers) : '—'}</td>
                <td>{fmtInt(s.buyersCount ?? 0)}</td>
                {/* Zero aqui é o RESULTADO (viram e não compraram), não dado faltando. */}
                <td>{fmtBRL(s.influencedRevenue ?? 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p style={{ color: '#6b7280', fontSize: 12, marginTop: 4 }}>
        Ranking por AUDIÊNCIA — inclui vídeo que não vendeu nada. Receita zero aqui é o resultado, não
        ausência de medição. Espectadores são ÚNICOS, não reproduções.
      </p>
    </div>
  )
}
